import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { MatSelectModule } from "@angular/material/select";
import { MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Subscription } from "rxjs";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { IPaginationResult, IRequestResult } from "app/common/interfaces";
import { ICreateTemporaryPassword, IUpdateTemporaryPassword } from "./dto/temporary-password.interface";
import { TemporaryPasswordService } from "./temporary-passwords.service";
import { TemporaryPasswordAttributes } from "@albatrosdeveloper/ave-models-package";
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { MatSidenavContainer } from "@angular/material/sidenav";
import { MasterUserPaginatedInterface } from "app/modules/components/sales/order-generate/interfaces/master-user.interface";
import { UserMasterService } from "app/modules/components/routing/sub-routes/sub-route-collaborator/user_master.service";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { getCode } from "app/utils/method";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { DatesFiltersComponent } from "app/modules/components/filters/dates-filters/dates-filters.component";
import { BusinessPartnersFiltersComponent } from "app/modules/components/filters/business-partners-filters/business-partners-filters.component";
import { assign } from "lodash";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { DateHelper } from "app/utils/date.helper";
import { StatusFiltersComponent } from "app/modules/components/filters/status-filters/status-filters.component";
import { TemporaryPasswordCreateComponent } from "../temporary-password-create/temporary-password-create.component";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { ColDef, GridConfig, GridReadyEvent, PageChangedEvent, SmartGridComponent, SortChangedEvent } from "app/shared/components/smart-grid";
import { TemporaryPasswordsActionsComponent } from "../temporary-passwords-actions/temporary-passwords-actions.component";
import { BooleanCellComponent } from "../temporary-password-render-fields/boolean-cell.component";
import { ExpiredCellComponent } from "../temporary-password-render-fields/expired-cell.component";
import { RenderDateComponent } from "../temporary-password-render-fields/render-date.component";

@Component({
  selector: "app-temporary-passwords",
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatButtonModule,
    MatMenuModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    HoverMenuDirective,
    MatSidenavContainer,
    MatInputModule,
    NgxMatSelectSearchModule,
    MatIconModule,
    FilterComponent,
    FilterSectionComponent,
    DatesFiltersComponent,
    BusinessPartnersFiltersComponent,
    StatusFiltersComponent,
    SmartGridComponent
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
  ],
  templateUrl: "./temporary-passwords.component.html",
  styleUrls: ["./temporary-passwords.component.scss"],
})
export class TemporaryPasswordsComponent implements OnInit, OnDestroy {
  getCode = getCode;
  //#region Table & Pagination
  columnDefs: ColDef<any>[] = [
    {
      field: 'targetUser.username',
      headerName: 'Usuario',
      sortable: true,
    },
    {
      field: 'requestedBy.username',
      headerName: 'Solicitado Por',
      sortable: true,
    },
    {
      field: 'reason',
      headerName: 'Motivo',
      sortable: true,
    },
    {
      field: 'createdAt',
      headerName: 'F. Creada',
      sortable: true,
      cellRenderer: RenderDateComponent
    }, 
    {
      field: 'expiresAt',
      headerName: 'F. Expirada',
      sortable: true,
      cellRenderer: RenderDateComponent
    }, 
    {
      field: 'expiresAtExpired',
      headerName: 'Expirada',
      sortable: true,
      cellRenderer: ExpiredCellComponent,
    },   
    {
      field: 'usedAt',
      headerName: 'Usada',
      sortable: true,
      cellRenderer: BooleanCellComponent,
    },
    {
      field: 'revoked',
      headerName: 'Revocada',
      sortable: true,
      cellRenderer: BooleanCellComponent,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      sortable: false,
      filterable: false,
      width: 150,
      cellRenderer: TemporaryPasswordsActionsComponent,
      cellRendererParams: {
        deletePassword: this.deletePassword.bind(this),
        revokePassword: this.revokePassword.bind(this),
        isExpired: this.isExpired.bind(this)
      }
    },
  ];

  gridConfig: GridConfig<any> = {
    emptyState: {
      message: 'No encontramos tus resultados!',
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: 'api',
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: 'api',
      multiColumn: false,
      defaultSort: [{ field: 'total' as any, direction: 'desc' }],
    },
    search: {
      enabled: false,
      mode: 'local'
    },
    columnFilters: {
      enabled: true,
      mode: 'local'
    },
    columnVisibility: {
      enabled: false,
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40,
    },
    styling: {
      maxHeight: '80vh',
      density: 'comfortable',
      fontSize: '12px',
      cellPaddingX: '3px',
      cellPaddingY: '0px',
    },
    settingsPanel: {
      enabled: false,
      position: 'right',
      enableColumnVisibility: true,
      enableDensity: false,
      enableExport: true,
      defaultDensity: 'comfortable',
    },
  };

  dataSource = new MatTableDataSource<TemporaryPasswordAttributes>([]);
  search: string = ''
  totalItems = 0;
  pageIndex = 1;
  pageSize = 50;
  sortActive: keyof TemporaryPasswordAttributes = "createdAt";
  sortDirection: "ASC" | "DESC" = "DESC";
  //#endregion

  //#region Form
  form: FormGroup;
  selectedPasswordId: string | null = null;
  selectedStatuses: number[] = [];
  //#endregion

  searchSubscription: Subscription;

  @ViewChild("actionModal") actionModal!: TemplateRef<any>;
  dialogRef: MatDialogRef<any>;

  originalAssignedUserList: MasterUserPaginatedInterface[] = [];
  filteredAssignedUserList: MasterUserPaginatedInterface[] = [];

  passwordForm!: FormGroup;

  loadBusinessPartnersData = false;

  constructor(
    private fb: FormBuilder,
    private temporaryPasswordService: TemporaryPasswordService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private userMasterService: UserMasterService,
    private cdr: ChangeDetectorRef,
    private readonly searchService: SearchService,
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      searchAssignedUser: [],
      assignedUserId: [],
      solicitedUserId: []
    });

    this.passwordForm = this.fb.group({
      targetUserId: [''],
      reason: [''],
      durationInHours: [4],
      searchAssignedUser: ['']
    });
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .subscribe(term => {
        this.pageIndex = 1
        this.search = term
        this.getList()
      });
  }

  clearFilters(): void {
    const today = new Date();
    this.form.patchValue({
      start: today,
      end: today,
      subRouteId: [],
      customerId: [],
      assignedUserId: [],
      searchAssignedUser: []
    })

    this.getList();
  }

  /**
       * Smart Grid: Handle grid ready event
       * Called when grid is fully initialized and rendered
       */
  onGridReady(event: GridReadyEvent<any>): void {
    // Auto-size all columns after grid is ready
    setTimeout(() => {
      event.api.autoSizeAllColumns();
    }, 100);
  }

  /**
     * Smart Grid: Handle page change event (API mode)
     */
  onPageChanged(event: PageChangedEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.getList();
  }

  /**
   * Smart Grid: Handle sort change event (API mode)
   */
  onSortChanged(event: SortChangedEvent<any>): void {
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as any;
      this.sortDirection = sortState.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.sortActive = 'createdAt';
      this.sortDirection = 'DESC';
    }
    this.getList();
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  ngOnInit(): void {
    this.onHandleSearch();
    this.getList();
  }

  isExpired(expiresAt: string | Date | null | undefined): boolean {
    if (!expiresAt) return false;
    const now = new Date();
    return new Date(expiresAt) < now;
  }

  //#region List & Pagination
  getList(): void {
    const options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      sort: this.sortActive,
      sortDirection: this.sortDirection,
      search: this.search
    };

    const filters: IFilters = {
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate: DateHelper.getEndOfDay(this.form.get("end")?.value) ?? DateHelper.getEndOfDay(this.form.get("start")?.value) ?? null,
      assignedUserId: this.form.get("assignedUserId")?.value || [],
      solicitedUserId: this.form.get("solicitedUserId")?.value || [],
    };

    let statusFilter: number[] = null;

    if (this.selectedStatuses.length > 0) {
      statusFilter = this.selectedStatuses;
    }


    this.temporaryPasswordService.findAllWithCount(options, filters, statusFilter).subscribe({
      next: (res: IPaginationResult<TemporaryPasswordAttributes>) => {
        this.dataSource.data = res?.data?.[0]?.data ?? [];
        this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
      },
      error: (err) => {
        this.snackBar.open(err.error?.message || "Error al obtener contraseñas temporales", "Cerrar", { duration: 5000 });
      }
    });
  }

  changeSort(event) {
    this.sortActive = event.active;
    this.sortDirection = event.direction?.toUpperCase() || "DESC";
    this.getList();
  }

  changePage(event: PageEvent) {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.getList();
  }
  //#endregion

  //#region Modal & Form
  openCreateModal(): void {
    const dialogRef = this.dialog.open(TemporaryPasswordCreateComponent, {
      width: '600px',
      disableClose: true,
      data: { selectedPasswordId: null }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getList();
      }
    });
  }

  revokePassword(password: TemporaryPasswordAttributes): void {
    this.temporaryPasswordService.revoked(password._id.toString()).subscribe({
      next: () => {
        this.snackBar.open('Contraseña revocada', 'Cerrar', { duration: 3000 });
        this.getList();
      },
      error: (err) => this.snackBar.open(err.error?.message || 'Error al revocar', 'Cerrar', { duration: 3000 })
    });
  }

  deletePassword(password: TemporaryPasswordAttributes): void {
    this.temporaryPasswordService.delete(password._id.toString()).subscribe({
      next: () => {
        this.snackBar.open('Contraseña eliminada', 'Cerrar', { duration: 3000 });
        this.getList();
      },
      error: (err) => this.snackBar.open(err.error?.message || 'Error al eliminar', 'Cerrar', { duration: 3000 })
    });
  }
  //#endregion

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

}
